import React from 'react';
import { cn } from './lib/utils';

/* Port of `~co/common/list` — inset rounded rows (Phase-1 look) with
   stretched ItemLink overlay and hover-revealed ItemActions.
   Marker classes `ui-item`/`ui-item-icon`/`ui-item-actions` let the
   accent-focus rule in global.css find them (see bottom of global.css). */

export const Item = React.forwardRef(function Item(
  {
    as = 'div',
    className,
    active,
    color,
    focusable,
    disabled,
    style,
    ...props
  },
  ref,
) {
  const Comp = as;
  return (
    <Comp
      role='listitem'
      {...props}
      ref={ref}
      data-active={active || undefined}
      data-disabled={disabled || undefined}
      data-focusable={focusable || undefined}
      className={cn(
        'ui-item group relative mx-[var(--padding-small)] flex h-[var(--list-item-height)] select-none items-center whitespace-nowrap rounded-lg px-[var(--padding-small)] text-[length:var(--primary-font-size)] leading-[2] text-inherit',
        '[&>:first-child]:mr-[var(--padding-small)] [&>:not(:last-child)]:mr-[var(--padding-small)]',
        '[@media(pointer:fine)]:hover:bg-hover data-[active=true]:bg-active focus-within:bg-active',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:text-disabled',
        Comp === 'a' && 'cursor-pointer',
        focusable && 'focusable',
        className,
      )}
      style={{ '--accent-color': color, ...style }}
    />
  );
});

export function ItemIcon({ className, ...props }) {
  return (
    <div
      {...props}
      className={cn(
        'ui-item-icon size-[var(--icon-size)] text-foreground [&>*]:block [&>*]:size-full [&>*]:object-cover [&_svg]:size-full',
        className,
      )}
    />
  );
}

export function ItemTitle({ className, ...props }) {
  return (
    <div
      {...props}
      className={cn(
        'min-w-0 flex-1 truncate py-px [&_em]:font-semibold [&_em]:not-italic',
        className,
      )}
    />
  );
}

export function ItemInfo({ className, ...props }) {
  return (
    <div
      {...props}
      className={cn(
        'flex flex-col text-right text-[length:var(--tertiary-font-size)] text-muted-foreground',
        className,
      )}
    />
  );
}

export function ItemActions({ className, ...props }) {
  return (
    <div
      {...props}
      className={cn(
        'ui-item-actions relative z-[1] mr-0 flex justify-end text-right [@media(pointer:fine)]:hidden [@media(pointer:fine)]:group-hover:flex',
        className,
      )}
    />
  );
}

export function ItemLink({ as = 'a', className, ...props }) {
  const Comp = as;
  return (
    <Comp
      {...props}
      className={cn('absolute inset-0 z-[1] cursor-pointer text-transparent', className)}
    />
  );
}

export function Section({
  className,
  active,
  isDragging,
  isDropping,
  disabled,
  ...props
}) {
  return (
    <div
      {...props}
      data-active={active || undefined}
      data-dragging={isDragging || undefined}
      data-dropping={isDropping || undefined}
      data-disabled={disabled || undefined}
      className={cn(
        'group flex min-h-[var(--list-item-height)] items-center px-[var(--padding-medium)] text-[length:var(--secondary-font-size)] text-muted-foreground',
        'data-[active=true]:bg-accent data-[active=true]:text-white',
        'data-[dragging=true]:bg-background data-[dragging=true]:shadow-[0_1px_3px_rgba(0,0,0,.1)]',
        'data-[dropping=true]:bg-accent/10 data-[dropping=true]:shadow-[inset_0_0_0_2px_var(--accent-color)] data-[dropping=true]:transition-[box-shadow,background]',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:text-disabled',
        className,
      )}
    />
  );
}

export function SectionTitle({ className, ...props }) {
  return (
    <div
      {...props}
      className={cn('min-w-0 flex-1 truncate pr-[var(--padding-small)]', className)}
    />
  );
}

export function SectionActions({ className, ...props }) {
  return (
    <div
      {...props}
      className={cn('hidden [@media(pointer:fine)]:group-hover:block', className)}
    />
  );
}

export function Wrap({ as = 'div', className, ...props }) {
  const Comp = as;
  return (
    <Comp
      {...props}
      className={cn(
        'overflow-x-hidden rounded-lg bg-background shadow-[inset_0_0_0_var(--shadow-height)_var(--shadow-color)] [&>:not(:last-child)]:shadow-[0_var(--shadow-height)_0_var(--shadow-color)]',
        className,
      )}
    />
  );
}
